import { InvalidStateError } from '../errors/internal-error';
import type { NonFunctional } from '../types/common';
import { isDefined } from './guards';
import { camelCase } from './string';

/**
 * Return array of specified length
 * @param {number} length
 * @returns {number[]}
 */
export const arrayOf = (length: number) => Array.from(Array(length).keys());

/**
 * Helper to produce an array of enum values.
 * @param enumeration Enumeration object.
 */
export function enumToArray<T extends {}>(enumeration: T): NonFunctional<T[keyof T]>[] {
	return Object.keys(enumeration)
		.filter((key) => Number.isNaN(Number(key)))
		.map((key) => (enumeration as any)[key])
		.filter((val) => typeof val === 'number' || typeof val === 'string') as NonFunctional<T[keyof T]>[];
}

/**
 * No-operation function
 * @return {void}
 */
export const noop = (): void => {};

/**
 * Silences error and returns fallback value if error is thrown
 * @param {(...args: any) => R} func
 * @param {F} fallback
 * @returns {R | F}
 */
export const silenceError = <R, F = undefined>(func: (...args: any) => R, fallback?: F): R | F => {
	try {
		return func();
	} catch (e) {
		return fallback as F;
	}
};

/**
 * Returns the given value if defined, otherwise throws an error
 * @param {T | undefined} value
 * @param {string} message
 * @returns {T}
 */
export const valOrThrow = <T>(value: T | undefined | null, message: string): NonNullable<T> => {
	if (!isDefined(value)) throw new InvalidStateError(message);
	return value;
};

/**
 * Maps slug-cased prop keys to camel-cased prop keys
 * @param {Record<string, string | number>} props
 * @returns {{[p: string]: string}}
 */
export const mapHtmlPropsToReact = (props: Record<string, string | number>) =>
	Object.fromEntries(Object.entries(props).map(([key, value]) => [camelCase(key), value]));

/**
 * Returns a value if defined or fallback value
 * @param {T | undefined | null} value
 * @param {T} fallback
 */
export const valOr = <TValue, TFallback>(value: TValue | undefined | null, fallback: TFallback): TValue | TFallback =>
	isDefined(value) ? value : fallback;

/**
 * A transformer version of valOr function
 * @param {T} fallback
 */
export const valOrTransformer =
	<TFallback>(fallback: TFallback) =>
	<TValue>(value: TValue | undefined | null) =>
		valOr<TValue, TFallback>(value, fallback);

/**
 * Simple IIFE to calculate value from getter
 * @param getter Getter function
 * @returns Value from the getter
 */
export const calcValue = <T>(getter: () => T) => getter();

/**
 * Typescript unknown type assertion helper
 * @param value Any value to assert as unknown
 * @returns Value as unknown
 */
export const asUnknown = <T = unknown>(value: any) => value as unknown as T;
